/**
 * Billing.ts — Stripe subscription management UI
 * Shows current plan, usage, upgrade options, and links to the Stripe customer portal.
 */

import { getSession } from '../session.js';
import { showToast } from './Toast.js';

type NavigateFn = (route: string, params?: Record<string, string>) => void;

const PLAN_NAMES: Record<string, string> = {
  starter:      'Starter',
  restaurant:   'Restaurant Suite',
  construction: 'Construction Suite',
  enterprise:   'Enterprise',
};

const PLAN_COLORS: Record<string, string> = {
  starter:      '#64748b',
  restaurant:   '#d4a017',
  construction: '#3b82f6',
  enterprise:   '#8b5cf6',
};

const PLAN_FEATURES: Record<string, string[]> = {
  starter: ['50 AI sessions / month', 'Digital PDF certificates', 'Audit trail', 'Template library', '1 user seat'],
  restaurant: ['Unlimited AI sessions (fair use)', 'POS & revenue tracking', 'Menu + tablet ordering', 'Kitchen display system', 'Table management', 'Staff scheduling & payroll', '3 user seats'],
  construction: ['Unlimited AI sessions (fair use)', 'WA SB 5838 compliance reports', 'Invoice management', 'Project financials', 'Document scanner', 'Payroll + employee mgmt', '5 user seats'],
  enterprise: ['Unlimited everything', 'SSO / SAML', 'White-label branding', 'API access', 'Dedicated support'],
};

export function renderBilling(container: HTMLElement, _navigate: NavigateFn): void {
  const session = getSession();
  if (!session) return;

  container.innerHTML = `
    <div class="page-header">
      <h1 class="page-title"><i class="bi bi-credit-card"></i> Billing &amp; Subscription</h1>
      <p class="page-subtitle">Manage your plan, usage, and payment details</p>
    </div>

    <!-- Current plan card -->
    <div class="card billing-plan-card" id="billing-plan-card">
      <div class="billing-loading">
        <span class="spinner"></span> Loading subscription…
      </div>
    </div>

    <!-- Plans comparison -->
    <div class="card" style="margin-top:24px">
      <div style="font-size:13px;font-weight:700;color:var(--text-secondary);text-transform:uppercase;letter-spacing:1px;margin-bottom:20px">
        <i class="bi bi-grid" style="color:var(--brand-gold)"></i> Available Plans
      </div>

      <div class="billing-plans-grid">
        ${buildPlanCards(session.industry ?? 'general')}
      </div>

      <p style="font-size:12px;color:var(--text-muted);margin-top:20px;text-align:center">
        All plans include a 14-day free trial · Annual plans save up to 17% · Verify certificates is always free
      </p>
    </div>

    <!-- Usage stats -->
    <div class="card" style="margin-top:24px" id="billing-usage-card">
      <div style="font-size:13px;font-weight:700;color:var(--text-secondary);text-transform:uppercase;letter-spacing:1px;margin-bottom:16px">
        <i class="bi bi-bar-chart" style="color:var(--brand-gold)"></i> This Month's Usage
      </div>
      <div class="billing-usage-grid">
        <div class="billing-usage-item">
          <div class="billing-usage-num" id="usage-sessions">—</div>
          <div class="billing-usage-label">AI Sessions</div>
        </div>
        <div class="billing-usage-item">
          <div class="billing-usage-num" id="usage-certs">—</div>
          <div class="billing-usage-label">Certificates Issued</div>
        </div>
        <div class="billing-usage-item">
          <div class="billing-usage-num" id="usage-docs">—</div>
          <div class="billing-usage-label">Documents Scanned</div>
        </div>
      </div>
    </div>

    <!-- Manage billing -->
    <div class="card" style="margin-top:24px">
      <div style="font-size:13px;font-weight:700;color:var(--text-secondary);text-transform:uppercase;letter-spacing:1px;margin-bottom:16px">
        <i class="bi bi-gear" style="color:var(--brand-gold)"></i> Manage Billing
      </div>
      <p style="font-size:14px;color:var(--text-secondary);margin-bottom:16px">
        Update your payment method, download invoices, or change your plan in the Stripe billing portal.
      </p>
      <button class="btn btn-primary" id="open-portal-btn">
        <i class="bi bi-box-arrow-up-right"></i> Open Billing Portal
      </button>
      <p style="font-size:12px;color:var(--text-muted);margin-top:12px">
        You'll be redirected to a secure Stripe page. Your payment information is never stored on PREUVU's servers.
      </p>
    </div>
  `;

  loadCurrentPlan(session.businessId);

  document.getElementById('open-portal-btn')?.addEventListener('click', () => openBillingPortal());

  document.querySelectorAll('.billing-plan-upgrade-btn[data-plan]').forEach(btn => {
    btn.addEventListener('click', () => {
      const plan    = btn.getAttribute('data-plan')!;
      const billing = (document.querySelector('.billing-billing-toggle .active') as HTMLButtonElement)?.dataset['billing'] ?? 'monthly';
      startCheckout(plan, billing as 'monthly' | 'annual');
    });
  });

  loadUsageStats(session.businessId);
}

function buildPlanCards(industry: string): string {
  const plans = [
    { id: 'starter', name: 'Starter', monthly: 79, annual: 69, recommended: false },
    { id: 'restaurant', name: 'Restaurant Suite', monthly: 99, annual: 82, recommended: industry === 'restaurant' },
    { id: 'construction', name: 'Construction Suite', monthly: 149, annual: 124, recommended: industry === 'construction' },
    { id: 'enterprise', name: 'Enterprise', monthly: null, annual: null, recommended: false },
  ];

  return plans.map(p => `
    <div class="billing-plan-card-item${p.recommended ? ' billing-plan-recommended' : ''}">
      ${p.recommended ? '<div class="billing-recommended-badge">Recommended for you</div>' : ''}
      <div class="billing-plan-item-name" style="color:${PLAN_COLORS[p.id] ?? 'var(--text-primary)'}">${p.name}</div>
      <div class="billing-plan-item-price">
        ${p.monthly ? `<span class="billing-price">$${p.monthly}</span><span class="billing-per">/mo</span>` : '<span class="billing-price-custom">Custom</span>'}
      </div>
      <ul class="billing-feature-list">
        ${(PLAN_FEATURES[p.id] ?? []).map(f => `<li><i class="bi bi-check-circle-fill" style="color:var(--success)"></i> ${f}</li>`).join('')}
      </ul>
      ${p.monthly
        ? `<button class="btn btn-ghost btn-sm billing-plan-upgrade-btn" data-plan="${p.id}" style="width:100%;margin-top:auto">
             Select Plan
           </button>`
        : `<button class="btn btn-ghost btn-sm" style="width:100%;margin-top:auto" onclick="window.location.href='mailto:sales@preuvu.com?subject=Enterprise%20Inquiry'">
             Contact Sales
           </button>`
      }
    </div>
  `).join('');
}

async function loadCurrentPlan(businessId: string): Promise<void> {
  const card = document.getElementById('billing-plan-card');
  if (!card) return;

  try {
    const res = await fetch(`/api/billing/subscription?businessId=${encodeURIComponent(businessId)}`);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const sub = await res.json() as {
      status: string;
      planId?: string;
      planName?: string;
      currentPeriodEnd?: string;
      cancelAtPeriodEnd?: boolean;
      trialEnd?: string;
    };

    if (sub.status === 'none' || !sub.planId) {
      card.innerHTML = buildNoPlanCard();
    } else {
      card.innerHTML = buildActivePlanCard(sub);
    }
  } catch {
    // In web mode without server, show upgrade prompt
    card.innerHTML = buildNoPlanCard();
  }
}

function buildActivePlanCard(sub: {
  status: string;
  planId?: string;
  planName?: string;
  currentPeriodEnd?: string;
  cancelAtPeriodEnd?: boolean;
  trialEnd?: string;
}): string {
  const statusColor: Record<string, string> = {
    active: 'var(--success)',
    trialing: '#d4a017',
    past_due: 'var(--danger)',
    canceled: 'var(--danger)',
  };

  const periodEnd = sub.currentPeriodEnd
    ? new Date(sub.currentPeriodEnd).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
    : null;

  const trialEnd = sub.trialEnd
    ? new Date(sub.trialEnd).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
    : null;

  return `
    <div style="display:flex;justify-content:space-between;align-items:flex-start;flex-wrap:wrap;gap:12px">
      <div>
        <div style="font-size:13px;font-weight:700;color:var(--text-secondary);text-transform:uppercase;letter-spacing:1px;margin-bottom:6px">
          Current Plan
        </div>
        <div style="font-size:22px;font-weight:800;color:${PLAN_COLORS[sub.planId ?? ''] ?? 'var(--text-primary)'}">
          ${sub.planName ?? PLAN_NAMES[sub.planId ?? ''] ?? 'Unknown Plan'}
        </div>
        <div style="font-size:13px;color:${statusColor[sub.status] ?? 'var(--text-muted)'};margin-top:4px">
          <i class="bi bi-circle-fill" style="font-size:8px"></i>
          ${sub.status === 'trialing' ? `Free trial · ends ${trialEnd ?? ''}` : sub.status.charAt(0).toUpperCase() + sub.status.slice(1)}
        </div>
        ${periodEnd ? `<div style="font-size:12px;color:var(--text-muted);margin-top:4px">
          ${sub.cancelAtPeriodEnd ? '⚠ Cancels' : 'Renews'} ${periodEnd}
        </div>` : ''}
      </div>
      <button class="btn btn-ghost btn-sm" id="open-portal-btn-plan">
        <i class="bi bi-pencil"></i> Manage Plan
      </button>
    </div>
    ${sub.status === 'past_due' ? `
    <div class="alert" style="margin-top:16px">
      <strong>⚠ Payment overdue.</strong> Please update your payment method to avoid service interruption.
      <button class="btn btn-primary btn-sm" style="margin-left:12px" onclick="document.getElementById('open-portal-btn').click()">Update Payment</button>
    </div>` : ''}
  `;
}

function buildNoPlanCard(): string {
  return `
    <div style="text-align:center;padding:20px 0">
      <div style="font-size:40px;margin-bottom:12px">◈</div>
      <h3 style="font-size:18px;font-weight:700;color:var(--text-primary);margin-bottom:8px">No active subscription</h3>
      <p style="font-size:14px;color:var(--text-secondary);margin-bottom:20px">
        Start your 14-day free trial — no credit card required.
      </p>
      <div style="display:flex;gap:12px;justify-content:center;flex-wrap:wrap">
        <button class="btn btn-gold" onclick="document.querySelector('.billing-plan-upgrade-btn[data-plan=\\'starter\\']').click()">
          Start Free Trial — Starter ($79/mo)
        </button>
      </div>
    </div>
  `;
}

async function startCheckout(planId: string, billing: 'monthly' | 'annual'): Promise<void> {
  const session = getSession();
  if (!session) { showToast('Please sign in first', 'error'); return; }

  showToast('Redirecting to checkout…', 'info');

  try {
    const res = await fetch('/api/billing/create-checkout', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        planId,
        billing,
        businessId: session.businessId,
        businessName: session.businessName,
        email: session.email ?? '',
        successUrl: `${window.location.origin}/?route=billing&checkout=success`,
        cancelUrl: `${window.location.origin}/?route=billing`,
      }),
    });

    const data = await res.json() as { success: boolean; url?: string; error?: string };
    if (data.success && data.url) {
      window.location.href = data.url;
    } else {
      showToast(data.error ?? 'Checkout failed', 'error');
    }
  } catch (err) {
    showToast((err as Error).message, 'error');
  }
}

async function openBillingPortal(): Promise<void> {
  const session = getSession();
  if (!session) { showToast('Please sign in first', 'error'); return; }

  const btn = document.getElementById('open-portal-btn') as HTMLButtonElement;
  if (btn) { btn.disabled = true; btn.innerHTML = '<span class="spinner"></span> Opening portal…'; }

  try {
    const res = await fetch('/api/billing/portal', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        businessId: session.businessId,
        returnUrl: window.location.href,
      }),
    });

    const data = await res.json() as { success: boolean; url?: string; error?: string };
    if (data.success && data.url) {
      window.location.href = data.url;
    } else {
      showToast(data.error ?? 'Could not open portal. Contact support@preuvu.com', 'error');
    }
  } catch (err) {
    showToast((err as Error).message, 'error');
  } finally {
    if (btn) { btn.disabled = false; btn.innerHTML = '<i class="bi bi-box-arrow-up-right"></i> Open Billing Portal'; }
  }
}

function loadUsageStats(businessId: string): void {
  // Pull from local session storage for now; server can provide real-time data
  const sessions = JSON.parse(localStorage.getItem(`preuve_sessions_${businessId}`) ?? '[]') as unknown[];
  const certs    = sessions.filter((s: unknown) => (s as { certId?: string }).certId).length;

  document.getElementById('usage-sessions')!.textContent = String(sessions.length);
  document.getElementById('usage-certs')!.textContent    = String(certs);
  document.getElementById('usage-docs')!.textContent     = '0';
}
